import { AsyncOpService } from 'util/services';
import { PhoenixToggleService } from 'phoenix/services/phoenixToggleService';
import { PhoenixUser } from 'phoenix/domain';

/**
* @ngdoc service
* @name  sharebase.sbUserService
* 
* @description 
* A service used to retrieve and interact with a ShareBase user
**/
export class UserService {
	public static $inject = [
		'$http',
		'$q',
		'$timeout',
		'asyncOpService',
		'pxUriPathService',		
		'pxPhoenixToggleService'
	];

	constructor(
		private $http:ng.IHttpService,
		private $q:ng.IQService,
		private $timeout:ng.ITimeoutService,
		private asyncOpService:AsyncOpService,
		private pxUriPathService,		
		private pxPhoenixToggleService:PhoenixToggleService
	) {
	}

	/**
	* @ngdoc method
	* @name sbUserService#getUser
	* @methodOf sharebase.sbUserService
	* @kind function
	*
	* @description
	* Gets a user
	*
	* @returns {Deferred} A promise that will resolve with a user
	* 
	*/
	public getUser(userId:number) {
		let uri = this.pxUriPathService.resolvePath(`/users/${userId}`);
		return this.$http.get<PhoenixUser>(uri).then(response => response.data);
	}
		
	/**
	* @ngdoc method
	* @name sbUserService#getUserActivity
	* @methodOf sharebase.sbUserService
	* @kind function
	*
	* @description
	* Gets a URL to a user's complete activity stream as a csv
	* 
	*/
	public getUserActivityUri(user:PhoenixUser, fileName:string, fromDate:Date, toDate:Date) {
		// TODO: Use Links property of user to get the proper endpoint when it returns it.
		const uri = '/async/users/{userId}/admin-report{?fromDate,toDate,fileName}';
		const config = { 
			headers: { 
				Accept: 'text/csv' 
			}
		};

		let resolvedPath = this.pxUriPathService.resolvePath(uri, {
			userId: user.UserId,
			fromDate: this.toDateString(fromDate),
			toDate: this.toDateString(toDate),
			fileName
		});

		return this.asyncOpService.getAsyncResultUri(resolvedPath, config);
	}

	private toDateString(date:Date){
		// getMonth() is zero-based
		return `${date.getMonth()+1}-${date.getDate()}-${date.getFullYear()}`;
	}

	/**
	* @ngdoc method
	* @name sbUserService#addUser
	* @methodOf sharebase.sbUserService
	* @kind function
	*
	* @description
	* Adds a new user
	*
	* @returns {Deferred} A promise that will resolve with a new user
	* 
	*/
	public addUser(user:PhoenixUser, deploymentId:number) {
		let uri = this.pxUriPathService.resolvePath('/{system}/users', this.setDeploymentParam(deploymentId));
		return this.$http
			.post<PhoenixUser>(uri, user)
			.then(response => response.data);
	}

	/**
	* @ngdoc method
	* @name sbUserService#removeUser
	* @methodOf sharebase.sbUserService
	* @kind function
	*
	* @description
	* Adds a new user
	*
	* @returns {Deferred} A promise that will resolve with a new user
	* 
	*/
	public removeUser(user:PhoenixUser, deploymentId:number, transferToUser:PhoenixUser, forceDelete:boolean = false) {
		let deleteParam = forceDelete ? '?force=true' : '?force=false';
		let transferToUserId = transferToUser ? transferToUser.UserId : null;
		let transferTarget = transferToUserId ? '&targetid=' + transferToUserId : '';
		let url = `/users/${user.UserId}${deleteParam}${transferTarget}`;

		return this.pxPhoenixToggleService.isEnabled('async-user-delete')
			.then(isEnabled => {
				let p: ng.IPromise<any>;

				if (isEnabled) {
					// Code in this block remains once async-delete-user feature toggle is removed
					url = this.pxUriPathService.resolvePath(`/{system}/async${url}`);
					return this.asyncOpService.request(url, {}, 'DELETE');
				} else {
					// Code in this block can be removed once async-delete-user feature toggle is removed
					url = this.pxUriPathService.resolvePath(url)
					p = this.$http.delete(url)
						.then(response => response.data);
				}

				return p;
			});		
	}

	/**
	* @ngdoc method
	* @name sbUserService#addEditGroupAdmin
	* @methodOf sharebase.sbUserService
	* @kind function
	*
	* @description
	* Adds a new Administrator (Tenant/DeploymentGroup)
	*
	* @returns {Deferred} A promise that will resolve with a new Administrator (Tenant/DeploymentGroup)
	* 
	*/
	public addEditGroupAdmin(deploymentGroupId:number, adminUser:PhoenixUser) {
		let uri = this.pxUriPathService.resolvePath(`/admin/deploymentgroups/${deploymentGroupId}/admins`);
		return this.$http
			.post(uri, adminUser)
			.then(response => response.data);
	}

	/**
	* @ngdoc method
	* @name sbUserService#removeGroupAdmin
	* @methodOf sharebase.sbUserService
	* @kind function
	*
	* @description
	* Removes the tenant administrator privileges for the specified deployment group from the specified user. 
	*
	* @returns {Deferred} A promise that will resolve with the updated Administrator
	* 
	*/
	public removeGroupAdmin(deploymentGroupId:number, userId:number) {
		let uri = this.pxUriPathService.resolvePath(`/admin/deploymentgroups/${deploymentGroupId}/admins/${userId}`);
		return this.$http.delete(uri)
			.then(response => response.data);
	}

	/**
	* @ngdoc method
	* @name sbUserService#updateUser
	* @methodOf sharebase.sbUserService
	* @kind function
	*
	* @description
	* Gets an updated user
	*
	* @returns {Deferred} A promise that will resolve with an updated user
	* 
	*/
	public updateUser(user:PhoenixUser, deploymentId:number) {
		return this.$http
			.patch(user.Links.Self, user, this.setDeploymentParam(deploymentId) as any)
			.then(function (response) {
				return response.data;
			});
	}

	/**
	* @ngdoc method
	* @name sbUserService#lockUser
	* @methodOf sharebase.sbUserService
	* @kind function
	*
	* @description
	* Locks a user
	*
	* @returns {Deferred} A promise that will resolve
	* 
	*/
	public lockUser(userId:number, deploymentId:number) {
		let uri = this.pxUriPathService.resolvePath('/{system}/users/' + userId + '/lock', this.setDeploymentParam(deploymentId));
		return this.$http.get(uri)
			.then(response => undefined);
	}

	/**
	* @ngdoc method
	* @name sbUserService#unlockUser
	* @methodOf sharebase.sbUserService
	* @kind function
	*
	* @description
	* Unlocks a user
	*
	* @returns {Deferred} A promise that will resolve
	* 
	*/
	public unlockUser(userId:number, deploymentId:number) {
		let uri = this.pxUriPathService.resolvePath(`/{system}/users/${userId}/unlock`, this.setDeploymentParam(deploymentId));
		return this.$http.get(uri)
			.then(function (response) {
				return response.data;
			});
	}

	private setDeploymentParam(deploymentId:number) {
		let result = {
			system: null
		};

		if (deploymentId) {
			result.system = deploymentId
		}

		return result;
	}
}


// WEBPACK FOOTER //
// ./src/sharebase/services/userService.ts